"use client"
import {
  ACTIVITY_SIDE_MODULE_STATE_COOKIE_NAME,
  COLLECTION_SIDE_MODULE_STATE_COOKIE_NAME,
  CREATED_FILTER_SIDE_PANEL_STATE_COOKIE_NAME,
  getDefaultSideModuleState,
  HOME_SIDE_MODULE_STATE_COOKIE_NAME,
  PORTFOLIO_SIDE_MODULE_STATE_COOKIE_NAME,
  PROFILE_SIDE_MODULE_STATE_COOKIE_NAME,
  type SideModuleState,
  STATS_SIDE_MODULE_STATE_COOKIE_NAME,
  STUDIO_FILTER_SIDE_PANEL_STATE_COOKIE_NAME,
  TOKEN_WATCHLIST_SIDE_MODULE_STATE_COOKIE_NAME,
  usePersona,
  WATCHLIST_SIDE_MODULE_STATE_COOKIE_NAME,
} from "@opensea/navigation"
import { useCookie, useOnChange } from "@opensea/ui-kit/hooks"
import { createContext, useContext } from "react"
import { LONG_LASTING_COOKIE_EXPIRATION } from "@/lib/cookie"
import type { FilterLayoutPage } from "./types"

type SideModulePage =
  | "home"
  | "portfolio"
  | "watchlist"
  | "created"
  | FilterLayoutPage

const PAGE_TO_COOKIE: Record<SideModulePage, string> = {
  home: HOME_SIDE_MODULE_STATE_COOKIE_NAME,
  collection: COLLECTION_SIDE_MODULE_STATE_COOKIE_NAME,
  profile: PROFILE_SIDE_MODULE_STATE_COOKIE_NAME,
  activity: ACTIVITY_SIDE_MODULE_STATE_COOKIE_NAME,
  stats: STATS_SIDE_MODULE_STATE_COOKIE_NAME,
  portfolio: PORTFOLIO_SIDE_MODULE_STATE_COOKIE_NAME,
  watchlist: WATCHLIST_SIDE_MODULE_STATE_COOKIE_NAME,
  "token-watchlist": TOKEN_WATCHLIST_SIDE_MODULE_STATE_COOKIE_NAME,
  created: CREATED_FILTER_SIDE_PANEL_STATE_COOKIE_NAME,
  studio: STUDIO_FILTER_SIDE_PANEL_STATE_COOKIE_NAME,
  token: HOME_SIDE_MODULE_STATE_COOKIE_NAME, // Use generic cookie for token pages
}

const PAGE_TO_DEFAULT_STATE: Partial<Record<SideModulePage, SideModuleState>> =
  {
    home: "open",
  }

const SideModuleContext = createContext<{
  isSideModuleOpen: boolean
  setIsSideModuleOpen: (open: boolean) => void
}>({
  isSideModuleOpen: false,
  setIsSideModuleOpen: () => undefined,
})

type Props = {
  children: React.ReactNode
  sideModuleState?: SideModuleState
  page: SideModulePage
}

export function SideModuleProvider({
  children,
  sideModuleState: initialSideModuleState,
  page,
}: Props) {
  const { persona } = usePersona()
  const sideModuleCookie = PAGE_TO_COOKIE[page]

  const defaultSideModuleState =
    PAGE_TO_DEFAULT_STATE[page] ?? getDefaultSideModuleState(persona)

  useOnChange(persona, () => {
    // If a default state is set for this page, we don't want changing the persona to override it
    if (PAGE_TO_DEFAULT_STATE[page]) {
      return
    }

    setIsSideModuleOpen(defaultSideModuleState === "open")
  })

  const [sideModuleState, setSideModuleStateCookie] =
    useCookie(sideModuleCookie)

  const resolvedSideModuleOpen =
    ((sideModuleState as SideModuleState | null) ??
      initialSideModuleState ??
      defaultSideModuleState) === "open"

  function setIsSideModuleOpen(open: boolean) {
    setSideModuleStateCookie(open ? "open" : "closed", {
      expires: LONG_LASTING_COOKIE_EXPIRATION,
    })
  }

  const value = {
    isSideModuleOpen: resolvedSideModuleOpen,
    setIsSideModuleOpen,
  }

  return (
    <SideModuleContext.Provider value={value}>
      {children}
    </SideModuleContext.Provider>
  )
}

export const useSideModule = () => useContext(SideModuleContext)
